import React from 'react';
import {connect} from 'react-redux';

import Grid from '@mui/material/Grid';

import isEmpty from 'is-empty';

import {Dispatch, Store} from 'spectra-logic-ui';
import Card from 'spectra-logic-ui/components/card';

import {Asset, ProductType, SelectOption} from '../common/types';
import Table from '../common/table';
import {fetchAssets, fetchProductTypes} from '../common/fetch';
import ButtonToolbar from '../common/button_toolbar';
import {SingleSelect} from 'spectra-logic-ui/components';
import {Form} from 'react-final-form';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import DeleteAsset from './delete';
import NewAsset from './new';
import EditAsset from './edit';

type Props = {
  items?: Asset[];
  productTypes?: ProductType[];
  error?: boolean;
  fetching?: boolean;
  fetchItems?: (productType?: string, version?: string) => Promise<any>;
  fetchProductTypes?: () => Promise<any>;
}

// Delay between the last keystroke in the version filter and the server request.
const VERSION_FILTER_DEBOUNCE_MS = 300;

type VersionFilterProps = {
  onChange: (version: string) => void;
  fetching?: boolean;
}

// Keeps the keystroke state local so typing only re-renders this field, not the
// whole assets table. The trimmed value is passed to onChange once typing pauses.
// A spinner is shown from the first keystroke until the resulting fetch completes.
const VersionFilter = ({onChange, fetching = false}: VersionFilterProps) => {
  const [value, setValue] = React.useState('');
  const [pending, setPending] = React.useState(false);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      onChange(value.trim());
      setPending(false);
    }, VERSION_FILTER_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [value]);

  const showSpinner = pending || fetching;
  return (
    <Box sx={{display: 'flex', alignItems: 'flex-end', gap: 1, flex: 'none'}}>
      <TextField
        label='Filter by Version'
        variant='standard'
        value={value}
        onChange={(event) => {
          setValue(event.target.value);
          setPending(true);
        }}
        sx={{width: '250px', flex: 'none'}}
      />
      {/* Fixed-size slot so the row does not shift when the spinner appears */}
      <Box sx={{width: 20, height: 20, mb: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
        {showSpinner && <CircularProgress size={16} />}
      </Box>
    </Box>
  );
};

const Assets = ({
  items = [],
  productTypes = [],
  error = false,
  fetching = false,
  fetchItems,
  fetchProductTypes,
}: Props) => {
  const [selectedItem, setSelectedItem] = React.useState({} as Asset);
  const [selectedProductType, setSelectedProductType] = React.useState<string | undefined>(undefined);
  const [versionFilter, setVersionFilter] = React.useState('');

  React.useEffect(() => {
    if (fetchProductTypes !== undefined) {
      fetchProductTypes();
    }
  }, []);

  React.useEffect(() => {
    if (fetchItems !== undefined) {
      fetchItems(selectedProductType, versionFilter || undefined);
    }
  }, [selectedProductType, versionFilter]);

  const productTypeOptions = [
    {key: '', text: 'All Product Types'},
    ...productTypes.map((pt) => ({key: pt.id, text: pt.name || pt.id})),
  ] as SelectOption[];
  return (
    <React.Fragment>
      <Grid container>
        <Grid item xs={12}>
          <Card>
            <Card.Body>
              <Box
                sx={{
                  position: 'sticky',
                  top: 65,
                  zIndex: 1,
                  backgroundColor: 'background.paper',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 2,
                }}
              >
                <ButtonToolbar>
                  <ButtonToolbar.CreateDialogButton title='New Asset' dialog={NewAsset} />
                  <ButtonToolbar.EditDialogButton
                    disabled={isEmpty(selectedItem)}
                    dialog={(props: any) => <EditAsset item={selectedItem} {...props} />}
                  />
                  <ButtonToolbar.DeleteDialogButton
                    disabled={isEmpty(selectedItem)}
                    dialog={(props: any) => (
                      <DeleteAsset item={selectedItem} {...props} />
                    )}
                  />
                </ButtonToolbar>
                <Box sx={{display: 'flex', alignItems: 'flex-end', gap: 2, mb: 2}}>
                  {/* SingleSelect stretches to fill its container, so constrain it here to keep it in the row */}
                  <Box sx={{width: '250px', flex: 'none'}}>
                    <Form
                      onSubmit={() => {}}
                      initialValues={{productType: ''}}
                      render={() => (
                        <SingleSelect
                          name='productType'
                          label='Filter by Product Type'
                          options={productTypeOptions}
                          onChange={(event: any) => {
                            setSelectedProductType(event.target.value || undefined);
                          }}
                        />
                      )}
                    />
                  </Box>
                  <VersionFilter onChange={setVersionFilter} fetching={fetching} />
                </Box>
              </Box>
              <Table>
                <Table.Header>
                  <Table.Row>
                    <Table.Cell>Product Type</Table.Cell>
                    <Table.Cell>Version</Table.Cell>
                    <Table.Cell>URL</Table.Cell>
                    <Table.Cell>Branch</Table.Cell>
                    <Table.Cell>File Type</Table.Cell>
                    <Table.Cell>File Name</Table.Cell>
                  </Table.Row>
                </Table.Header>
                <Table.Body isLoading={fetching} hasError={error}>
                  {items && items.map((item) => (
                    <Table.Row
                      key={item.id} selected={item.id === selectedItem.id}
                      onClick={() => setSelectedItem(item)}
                    >
                      <Table.Cell>{item.productType}</Table.Cell>
                      <Table.Cell>{item.version}</Table.Cell>
                      <Table.Cell>{item.url}</Table.Cell>
                      <Table.Cell>{item.branch}</Table.Cell>
                      <Table.Cell>{item.fileType}</Table.Cell>
                      <Table.Cell>{item.fileName}</Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </Card.Body>
          </Card>
        </Grid>
      </Grid>
    </React.Fragment>
  );
};

const mapStateToProps = (state: Store) => {
  const items = state.resources['assets'] || {};
  const productTypes = state.resources['product_types'] || {};
  return {
    items: items.data || [],
    productTypes: productTypes.data || [],
    error: items.error,
    fetching: items.fetching,
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => {
  return ({
    fetchItems: (productType?: string, version?: string) => fetchAssets(dispatch, productType, version),
    fetchProductTypes: () => fetchProductTypes(dispatch),
  });
};

export default connect(mapStateToProps, mapDispatchToProps)(Assets);
